// MemoryPage — the Brain surface.
//
// One agent picker + four lenses:
//   1. Session Summary   — living kb-doc/agents/<agent>/session-summary, per agent (default).
//   2. Session Logs      — type=session_log, the append-only diary (the raw trail).
//   3. Company Learnings — the ONE shared kb-doc/company/learnings (no agent) —
//                          the shared brain every agent reads on hydrate.
//   4. Index             — live force-directed graph of the cross-link web.
//
// The per-agent Learnings tab + Learnings Summary were retired (2026-07-08):
// a lesson is either GENERAL (→ Company Learnings) or PROJECT-specific (→ the
// project summary). The `memories` table is now just the session_log trail.

import { useEffect, useMemo, useState } from "react";
import { Brain, AlertCircle, ScrollText, FileText, BookOpen, Network } from "lucide-react";
import { useApi } from "@/lib/api";
import { BlockRenderer, type Block } from "../blueprint/BlockRenderer";
import { architectureConfig } from "../blueprint/ArchitectureBlocks";
import { MemoryGraphView } from "./MemoryGraphView";

type MemoryType =
  | "session_log"
  | "learning"
  | "user_fact"
  | "area"
  | "principle"
  | "identity";

type MemoryRow = {
  id: string;
  agent: string | null;
  type: MemoryType;
  title: string | null;
  body: string;
  metadata: Record<string, unknown>;
  created_at: string;
};

type TabKey = "logs" | "session_summary" | "company_learnings" | "index";

// A persona chip. Derived live from the agents table (/api/agents) — NO
// hardcoded persona list anywhere on this page (MJ-227). Adding an agent row
// makes it appear here with zero code changes, same doctrine as the
// data-driven KB index (MJOS-143).
type AgentChip = { slug: string; label: string; color: string | null };

const TABS: { key: TabKey; label: string; icon: typeof Brain }[] = [
  { key: "session_summary", label: "Session Summary", icon: FileText },
  { key: "logs", label: "Session Logs", icon: ScrollText },
  { key: "company_learnings", label: "Company Learnings", icon: BookOpen },
  { key: "index", label: "Index", icon: Network },
];

const TYPE_STYLES: Record<MemoryType, string> = {
  session_log:
    "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  learning:
    "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  user_fact:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  area: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400",
  principle:
    "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400",
  identity: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400",
};

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function MemoryList({
  rows,
  emptyHint,
  colorBySlug,
}: {
  rows: MemoryRow[] | null;
  emptyHint: string;
  colorBySlug: Record<string, string | null>;
}) {
  if (rows === null) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  if (rows.length === 0) {
    return (
      <div className="rounded-xl border bg-muted/30 p-8 text-center">
        <p className="text-sm font-medium">Nothing here yet</p>
        <p className="mt-1 text-xs text-muted-foreground">{emptyHint}</p>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {rows.map((m) => (
        <div
          key={m.id}
          className="rounded-xl border bg-card p-4 transition-colors hover:border-foreground/20"
        >
          <div className="mb-2 flex items-start justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${TYPE_STYLES[m.type]}`}
              >
                {m.type.replace("_", " ")}
              </span>
              {m.agent && (
                <span
                  className="text-xs font-semibold"
                  style={{ color: colorBySlug[m.agent] ?? undefined }}
                >
                  {m.agent}
                </span>
              )}
              {m.title && (
                <span className="text-sm font-medium">{m.title}</span>
              )}
            </div>
            <span className="shrink-0 text-[11px] text-muted-foreground">
              {formatTimestamp(m.created_at)}
            </span>
          </div>
          <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground/90">
            {m.body}
          </pre>
        </div>
      ))}
    </div>
  );
}

// Renders a kb-doc page for the selected agent (Session Summary) or a fixed
// shared page (Company Learnings, via overrideSlug).
function SummaryView({
  agent,
  agentLabel,
  kind,
  overrideSlug,
  overrideLabel,
}: {
  agent: string;
  agentLabel: string;
  kind: "session-summary" | "learnings-summary";
  overrideSlug?: string;
  overrideLabel?: string;
}) {
  const api = useApi();
  const slug = overrideSlug ?? `kb-doc/agents/${agent}/${kind}`;
  const [blocks, setBlocks] = useState<Block[] | null>(null);
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setBlocks(null);
    setMissing(false);
    setError(null);
    (async () => {
      try {
        const res = await api(`/api/kb/${slug}`);
        if (res.status === 404) {
          if (!cancelled) setMissing(true);
          return;
        }
        if (!res.ok) {
          if (!cancelled) setError(`HTTP ${res.status}`);
          return;
        }
        const body = (await res.json()) as {
          content?: { blocks?: Block[] };
        };
        if (cancelled) return;
        if (Array.isArray(body.content?.blocks)) {
          // filter out any null/malformed elements so one bad block can't crash the page
          setBlocks((body.content?.blocks ?? []).filter(Boolean) as Block[]);
        } else {
          setMissing(true);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (error) {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-400">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>Failed to load summary: {error}</span>
      </div>
    );
  }
  if (missing) {
    const kindLabel = overrideLabel ?? (kind === "session-summary" ? "Session Summary" : "Learnings Summary");
    return (
      <div className="rounded-xl border bg-muted/30 p-8 text-center">
        <Brain className="mx-auto mb-2 h-8 w-8 text-muted-foreground/60" />
        <p className="text-sm font-medium">
          No {kindLabel.toLowerCase()} yet{overrideSlug ? "" : ` for ${agentLabel}`}.
        </p>
        <p className="mt-2 text-xs text-muted-foreground">
          The agent maintains this at run end.
        </p>
      </div>
    );
  }
  if (blocks === null) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  return <BlockRenderer config={architectureConfig} blocks={blocks} />;
}

export function MemoryPage() {
  const api = useApi();
  const [agents, setAgents] = useState<AgentChip[] | null>(null);
  const [agent, setAgent] = useState<string>("");
  const [tab, setTab] = useState<TabKey>("session_summary");
  const [rows, setRows] = useState<MemoryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Derive the persona chips from the agents table. Jarvis first (the default),
  // then the rest alphabetically. No hardcoded list — a new agents row shows
  // up here automatically.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api("/api/agents");
        if (!res.ok) {
          if (!cancelled) setError(`HTTP ${res.status}`);
          return;
        }
        const data = (await res.json()) as {
          name: string;
          display_name: string;
          color: string | null;
        }[];
        const chips: AgentChip[] = data
          .map((a) => ({ slug: a.name, label: a.display_name || a.name, color: a.color }))
          .sort((a, b) => {
            if (a.slug === "jarvis") return -1;
            if (b.slug === "jarvis") return 1;
            return a.label.localeCompare(b.label);
          });
        if (cancelled) return;
        setAgents(chips);
        setAgent((prev) => prev || chips[0]?.slug || "");
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch the selected agent's memories server-side (the endpoint filters by
  // ?agent=). This keeps every agent's rows reachable regardless of how many
  // rows newer/busier agents have — a global LIMIT would otherwise bury a
  // low-volume agent like Leo.
  useEffect(() => {
    if (!agent) return;
    let cancelled = false;
    setRows(null);
    (async () => {
      try {
        const res = await api(`/api/memories?agent=${encodeURIComponent(agent)}`);
        if (!res.ok) {
          if (!cancelled) setError(`HTTP ${res.status}`);
          return;
        }
        const data = (await res.json()) as MemoryRow[];
        if (!cancelled) setRows(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent]);

  const colorBySlug = useMemo(
    () => Object.fromEntries((agents ?? []).map((a) => [a.slug, a.color])),
    [agents],
  );
  const agentLabel = useMemo(
    () => agents?.find((a) => a.slug === agent)?.label ?? agent,
    [agents, agent],
  );

  const logs = useMemo(
    () => rows?.filter((m) => m.type === "session_log") ?? null,
    [rows],
  );

  return (
    <div className="w-full h-svh overflow-auto bg-background">
      <div className="border-b px-6 py-5 flex items-center gap-3">
        <Brain className="h-5 w-5 text-foreground/80" />
        <div className="flex-1">
          <h1 className="text-xl font-semibold leading-tight">The Brain</h1>
          <p className="text-xs text-muted-foreground">
            Per-agent session summary + session logs, the shared Company
            Learnings, and the live cross-link index.
          </p>
        </div>
      </div>

      {/* Agent picker */}
      <div className="border-b px-6 py-3 flex items-center gap-2 flex-wrap">
        <span className="text-xs uppercase tracking-wider text-muted-foreground/70">
          Agent
        </span>
        <div className="flex items-center gap-1 flex-wrap">
          {agents === null ? (
            <span className="text-xs text-muted-foreground">Loading…</span>
          ) : (
            agents.map((a) => {
              const active = agent === a.slug;
              return (
                <button
                  key={a.slug}
                  type="button"
                  onClick={() => setAgent(a.slug)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                    active ? "bg-foreground text-background" : "hover:bg-accent"
                  }`}
                  style={active ? undefined : { color: a.color ?? undefined }}
                >
                  {a.label}
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b px-6 flex flex-wrap items-center gap-0.5">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors ${
                active
                  ? "border-foreground text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="px-6 py-5">
        {error && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-400">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Failed to load: {error}</span>
          </div>
        )}

        {tab === "session_summary" && (
          <SummaryView agent={agent} agentLabel={agentLabel} kind="session-summary" />
        )}
        {tab === "logs" && (
          <MemoryList
            rows={logs}
            colorBySlug={colorBySlug}
            emptyHint={`Session logs for ${agentLabel} will appear here as the Stop hook captures them.`}
          />
        )}
        {tab === "company_learnings" && (
          <SummaryView
            agent={agent}
            agentLabel="the company"
            kind="learnings-summary"
            overrideSlug="kb-doc/company/learnings"
            overrideLabel="Company Learnings"
          />
        )}
        {tab === "index" && <MemoryGraphView />}
      </div>
    </div>
  );
}

(MemoryPage as unknown as { path: string }).path = "/memory";
